import { useState, useEffect, useCallback } from 'react';
import izenuAPI from '../../services/izenuAPI';

// Financeiro → Pagamentos a artistas.
// Balance per payee = artist's share of direct sales (ArtistEarnings::SHARE_PCT,
// fixed per sale) + streaming royalties of closed months − what was paid. The
// payee is the artist, or the distributor when it has payout_to 'distributor'
// (external labels, which pay their artists themselves). Payees ask for their
// balance (Pedidos); the team transfers outside the system and records it here.
// Campaign incentives are recorded here too, but never touch the balance.
// A payout is voided, never deleted.

const PURPLE = '#6C3FC5';
const METHOD_LABEL = { bank_transfer: 'Transferência', multicaixa_express: 'Multicaixa Express', other: 'Outro' };
const INCENTIVES = [20000, 50000, 100000];

const money = (v, c) => {
  const n = Number(v) || 0;
  if (c === 'AOA') return `${n.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} Kz`;
  return n.toLocaleString('pt-PT', { style: 'currency', currency: c || 'USD' });
};
const fmtDate = (s) => (s ? new Date(String(s).replace(' ', 'T')).toLocaleDateString('pt-PT') : '—');
const payeeLabel = (p) => (p ? `${p.type === 'distributor' ? 'Distribuidora · ' : ''}${p.name || `#${p.id}`}` : '');
const payeeKey = (p) => `${p.type}-${p.id}`;
const REQUEST_STATUS = { pending: ['Pendente', '#B26A00'], paid: ['Pago', '#2E7D32'], rejected: ['Rejeitado', '#C62828'], cancelled: ['Cancelado', '#999'] };
const today = () => new Date().toISOString().slice(0, 10);

const box = { background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8 };
const th = { textAlign: 'left', padding: '10px 8px', fontSize: 12, color: '#666', fontWeight: 600, borderBottom: '1px solid #e5e5e5', whiteSpace: 'nowrap' };
const td = { padding: '10px 8px', fontSize: 13, color: '#333', borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' };
const input = { padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box' };
const label = { display: 'block', fontSize: 12, color: '#666', marginBottom: 4 };

function AccountBox({ account }) {
  if (!account) {
    return <div style={{ padding: 12, background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 6, fontSize: 13 }}>Sem dados bancários no Estúdio — não é possível pagar.</div>;
  }
  return (
    <div style={{ padding: 12, background: '#F7F5FC', border: '1px solid #E4DCF5', borderRadius: 6, fontSize: 13, lineHeight: 1.6 }}>
      <div><b>{account.holder_name || '—'}</b>{account.tax_id ? ` · NIF ${account.tax_id}` : ''}</div>
      {account.method === 'multicaixa_express'
        ? <div>Multicaixa Express: <b>{account.mcx_phone}</b></div>
        : <div>{account.bank_name || 'Banco —'} · <b style={{ fontFamily: 'monospace' }}>{account.iban || '—'}</b>{account.swift_bic ? ` · ${account.swift_bic}` : ''}</div>}
      {!account.complete && <div style={{ color: '#B26A00' }}>Dados incompletos (registo antigo).</div>}
    </div>
  );
}

function ArtistDetail({ payee, onClose, onChanged }) {
  const isDist = payee.type === 'distributor';
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ type: 'sales', currency: 'AOA', amount: '', method: 'bank_transfer', paid_at: today(), reference: '', release_id: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const r = await izenuAPI.get('/admin/artist_payouts/list.php', { params: isDist ? { distributor_id: payee.id } : { artist_id: payee.id } });
    setD(r.data);
    const aoa = (r.data.balances || []).find((b) => b.currency === 'AOA');
    setForm((f) => ({ ...f, amount: f.type === 'sales' && aoa ? String(Math.max(0, aoa.available)) : f.amount }));
  }, [isDist, payee.id]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === 'type' || k === 'currency') {
      if (next.type === 'sales') {
        const b = (d?.balances || []).find((x) => x.currency === next.currency);
        next.amount = b ? String(Math.max(0, b.available)) : '';
      } else {
        next.amount = '';
      }
    }
    return next;
  });

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      await izenuAPI.post('/admin/artist_payouts/save.php', { ...form, ...(isDist ? { distributor_id: payee.id } : { artist_id: payee.id }) });
      setForm((f) => ({ ...f, reference: '', notes: '', release_id: '' }));
      await load();
      onChanged();
      setMsg('Pagamento registado.');
    } catch (e) {
      setMsg(e.response?.data?.message || 'Não foi possível registar.');
    } finally {
      setSaving(false);
    }
  };

  const voidPayout = async (id) => {
    if (!window.confirm('Anular este pagamento? O valor volta ao saldo a receber (e o pedido de levantamento, se houver, volta a pendente).')) return;
    try {
      await izenuAPI.post('/admin/artist_payouts/save.php', { void: id });
      await load();
      onChanged();
    } catch (e) {
      setMsg(e.response?.data?.message || 'Não foi possível anular.');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 'min(760px, 100%)', height: '100%', overflowY: 'auto', background: '#fafafa', padding: 28 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#222' }}>{d ? payeeLabel(d.payee) : 'A carregar…'}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: '#666' }}>×</button>
        </div>
        {d && (
          <>
            <div style={{ fontSize: 13, color: '#777', marginBottom: 12 }}>{d.payee?.email} {d.payee?.phone ? `· ${d.payee.phone}` : ''}</div>
            {d.paid_through && (
              <div style={{ padding: 12, background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
                Este artista é pago através da <b>{d.paid_through.name}</b>: as vendas e os royalties dele entram no saldo da distribuidora. O saldo abaixo só tem o que lhe foi atribuído directamente (por exemplo, incentivos).
              </div>
            )}
            <AccountBox account={d.account} />

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0' }}>
              {(d.balances || []).length === 0 && <div style={{ fontSize: 13, color: '#999' }}>Sem vendas nem royalties.</div>}
              {(d.balances || []).map((b) => (
                <div key={b.currency} style={{ ...box, padding: '12px 16px', flex: '1 1 200px' }}>
                  <div style={{ fontSize: 12, color: '#777' }}>A pagar ({b.currency})</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: b.balance > 0 ? '#2E7D32' : '#222' }}>{money(b.balance, b.currency)}</div>
                  <div style={{ fontSize: 12, color: '#999' }}>
                    Vendas de álbuns {money(b.sales_earned, b.currency)} ({b.sales}){b.royalties_earned > 0 ? ` · royalties de streaming ${money(b.royalties_earned, b.currency)}` : ''} · pago {money(b.paid, b.currency)}
                  </div>
                  {b.pending > 0 && <div style={{ fontSize: 12, color: '#B26A00' }}>{money(b.pending, b.currency)} pedidos para levantamento</div>}
                </div>
              ))}
            </div>

            <div style={{ ...box, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, marginBottom: 12, color: '#333' }}>Registar pagamento</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                <div>
                  <label style={label}>Tipo</label>
                  <select style={input} value={form.type} onChange={(e) => set('type', e.target.value)}>
                    <option value="sales">Saldo (vendas e royalties)</option>
                    {!isDist && <option value="incentive">Incentivo da campanha</option>}
                  </select>
                </div>
                <div>
                  <label style={label}>Moeda</label>
                  <select style={input} value={form.currency} onChange={(e) => set('currency', e.target.value)}>
                    <option value="AOA">Kz (AOA)</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div>
                  <label style={label}>Valor</label>
                  {form.type === 'incentive' && form.currency === 'AOA' ? (
                    <select style={input} value={form.amount} onChange={(e) => set('amount', e.target.value)}>
                      <option value="">Escolher…</option>
                      {INCENTIVES.map((v) => <option key={v} value={v}>{money(v, 'AOA')}</option>)}
                    </select>
                  ) : (
                    <input style={input} type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} />
                  )}
                </div>
                <div>
                  <label style={label}>Como</label>
                  <select style={input} value={form.method} onChange={(e) => set('method', e.target.value)}>
                    {Object.entries(METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={label}>Data do pagamento</label>
                  <input style={input} type="date" max={today()} value={form.paid_at} onChange={(e) => set('paid_at', e.target.value)} />
                </div>
                <div>
                  <label style={label}>Referência / comprovativo</label>
                  <input style={input} value={form.reference} onChange={(e) => set('reference', e.target.value)} placeholder="Nº da transferência" />
                </div>
                {form.type === 'incentive' && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={label}>Lançamento premiado</label>
                    <select style={input} value={form.release_id} onChange={(e) => set('release_id', e.target.value)}>
                      <option value="">Escolher…</option>
                      {(d.releases || []).filter((r) => r.type !== 'single').map((r) => <option key={r.id} value={r.id}>{r.title} ({r.type})</option>)}
                    </select>
                  </div>
                )}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={label}>Notas internas</label>
                  <input style={input} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
                <button
                  onClick={save}
                  disabled={saving || !form.amount || !d.account || (form.type === 'incentive' && !form.release_id)}
                  style={{ padding: '9px 18px', borderRadius: 6, border: 'none', background: PURPLE, color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: saving || !form.amount || !d.account ? 0.5 : 1 }}
                >
                  {saving ? 'A registar…' : 'Registar pagamento'}
                </button>
                {msg && <span style={{ fontSize: 13, color: msg === 'Pagamento registado.' ? '#2E7D32' : '#C62828' }}>{msg}</span>}
              </div>
              <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                Isto só regista o pagamento — a transferência faz-se no banco ou no Multicaixa. Para responder a um pedido de levantamento, usa o separador Pedidos.
              </div>
            </div>

            <div style={{ fontWeight: 700, color: '#333', marginBottom: 8 }}>Pagamentos</div>
            <div style={{ ...box, overflowX: 'auto', marginBottom: 20 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Data</th><th style={th}>Tipo</th><th style={th}>Valor</th><th style={th}>Como / referência</th><th style={th}>Para</th><th style={th}></th></tr></thead>
                <tbody>
                  {(d.payouts || []).length === 0 && <tr><td style={{ ...td, color: '#999' }} colSpan={6}>Nenhum pagamento registado.</td></tr>}
                  {(d.payouts || []).map((p) => (
                    <tr key={p.id} style={{ opacity: p.voided_at ? 0.45 : 1 }}>
                      <td style={td}>{fmtDate(p.paid_at)}</td>
                      <td style={td}>{p.type === 'incentive' ? `Incentivo${p.release_title ? ` · ${p.release_title}` : ''}` : 'Saldo'}{p.request_id && <div style={{ color: '#777', fontSize: 11 }}>Pedido #{p.request_id}</div>}{p.voided_at && <div style={{ color: '#C62828', fontSize: 11 }}>Anulado</div>}</td>
                      <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>{money(p.amount, p.currency)}</td>
                      <td style={td}>{METHOD_LABEL[p.method] || p.method}{p.reference && <div style={{ color: '#777', fontSize: 12 }}>{p.reference}</div>}{p.notes && <div style={{ color: '#999', fontSize: 12 }}>{p.notes}</div>}</td>
                      <td style={{ ...td, fontSize: 12, color: '#777' }}>{p.account_snapshot || '—'}</td>
                      <td style={td}>{!p.voided_at && <button onClick={() => voidPayout(p.id)} style={{ border: '1px solid #ddd', background: '#fff', borderRadius: 4, fontSize: 12, padding: '3px 8px', cursor: 'pointer', color: '#C62828' }}>Anular</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(d.requests || []).length > 0 && (
              <>
                <div style={{ fontWeight: 700, color: '#333', marginBottom: 8 }}>Pedidos de levantamento</div>
                <div style={{ ...box, overflowX: 'auto', marginBottom: 20 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={th}>Pedido</th><th style={th}>Valor</th><th style={th}>Estado</th><th style={th}>Para</th></tr></thead>
                    <tbody>
                      {d.requests.map((r) => (
                        <tr key={r.id}>
                          <td style={td}>#{r.id} · {fmtDate(r.requested_at)}</td>
                          <td style={{ ...td, fontWeight: 600 }}>{money(r.amount, r.currency)}</td>
                          <td style={{ ...td, color: REQUEST_STATUS[r.status][1] }}>{REQUEST_STATUS[r.status][0]}{r.note && <div style={{ color: '#777', fontSize: 12 }}>{r.note}</div>}</td>
                          <td style={{ ...td, fontSize: 12, color: '#777' }}>{r.account_snapshot || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {(d.royalties || []).length > 0 && (
              <>
                <div style={{ fontWeight: 700, color: '#333', marginBottom: 8 }}>Royalties (meses fechados)</div>
                <div style={{ ...box, overflowX: 'auto', marginBottom: 20 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={th}>Mês</th>{isDist && <th style={th}>Artista</th>}<th style={th}>Reproduções pagas</th><th style={th}>Royalties</th></tr></thead>
                    <tbody>
                      {d.royalties.map((r) => (
                        <tr key={`${r.period}-${r.artist_id || ''}`}>
                          <td style={td}>{r.period}</td>
                          {isDist && <td style={td}>{r.artist_name || `#${r.artist_id}`}</td>}
                          <td style={td}>{Number(r.streams).toLocaleString('pt-PT')}</td>
                          <td style={{ ...td, fontWeight: 600 }}>{money(r.amount_aoa, 'AOA')}{!isDist && r.payee_distributor_id && <span style={{ color: '#999', fontWeight: 400 }}> (à distribuidora)</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div style={{ fontWeight: 700, color: '#333', marginBottom: 8 }}>Vendas desde {fmtDate(d.since)}</div>
            <div style={{ ...box, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Data</th><th style={th}>Lançamento</th>{isDist && <th style={th}>Artista</th>}<th style={th}>Pago pelo fã</th><th style={th}>Parte do artista</th></tr></thead>
                <tbody>
                  {(d.sales || []).length === 0 && <tr><td style={{ ...td, color: '#999' }} colSpan={5}>Sem vendas.</td></tr>}
                  {(d.sales || []).map((s) => (
                    <tr key={s.id}>
                      <td style={td}>{fmtDate(s.created_at)}</td>
                      <td style={td}>{s.release_title}</td>
                      {isDist && <td style={td}>{s.artist_name}</td>}
                      <td style={td}>{money(s.amount, s.currency)}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{money(s.artist_amount, s.currency)} <span style={{ color: '#999', fontWeight: 400 }}>({Number(s.artist_share_pct)}%)</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ArtistPayouts() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ installed: true, share_pct: 70, since: '', min_withdrawal: { AOA: 20000 } });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [onlyDue, setOnlyDue] = useState(false);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') === 'balances' ? 'balances' : 'requests');

  const load = useCallback(async () => {
    try {
      const r = await izenuAPI.get('/admin/artist_payouts/list.php');
      setRows(r.data.data || []);
      setMeta({ installed: r.data.installed !== false, share_pct: r.data.share_pct, since: r.data.since, min_withdrawal: r.data.min_withdrawal || { AOA: 20000 } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = {};
  rows.forEach((r) => (r.balances || []).forEach((b) => { totals[b.currency] = (totals[b.currency] || 0) + Math.max(0, b.balance); }));
  const shown = onlyDue ? rows.filter((r) => (r.balances || []).some((b) => b.balance > 0)) : rows;
  const pendingCount = rows.reduce((n, r) => n + (r.pending_requests || 0), 0);
  const tabBtn = (k, label) => (
    <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 14px', border: 'none', borderBottom: `2px solid ${tab === k ? PURPLE : 'transparent'}`, background: 'none', fontWeight: 600, fontSize: 14, color: tab === k ? '#222' : '#777', cursor: 'pointer' }}>{label}</button>
  );

  return (
    <div style={{ padding: 40 }}>
      <h2 style={{ color: '#333', marginBottom: 6, fontSize: 22, fontWeight: 700 }}>Pagamentos a artistas</h2>
      <p style={{ color: '#666', marginBottom: 24, fontSize: 14 }}>
        Saldo = <b>{meta.share_pct}%</b> de cada venda de lançamento (sobre o preço pago pelo fã, vendas desde {fmtDate(meta.since)}; compras do próprio
        artista não contam) + royalties de streaming dos meses fechados (Financeiro → Royalties de streaming) − pagamentos. Os artistas de distribuidoras externas são pagos
        à distribuidora. O artista ou a distribuidora pede o levantamento a partir de {money(meta.min_withdrawal.AOA || 20000, 'AOA')}.
        Os incentivos da campanha 100mil registam-se aqui à parte.
      </p>

      {!meta.installed && (
        <div style={{ padding: 16, background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
          Correr <code>php bin/migration_artist_earnings.php</code> e <code>php bin/migration_payouts.php</code> no servidor.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        {Object.keys(totals).length === 0 && !loading && <div style={{ ...box, padding: '12px 16px', fontSize: 14, color: '#777' }}>Nada a pagar.</div>}
        {Object.entries(totals).map(([c, v]) => (
          <div key={c} style={{ ...box, padding: '12px 16px', minWidth: 200 }}>
            <div style={{ fontSize: 12, color: '#777' }}>Total a pagar ({c})</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#222' }}>{money(v, c)}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e5e5', marginBottom: 16 }}>
        {tabBtn('requests', `Pedidos${pendingCount ? ` (${pendingCount})` : ''}`)}
        {tabBtn('balances', 'Saldos')}
      </div>

      {tab === 'requests' && <Requests onChanged={load} onOpen={setOpen} />}

      {tab === 'balances' && (
        <>
          <label style={{ fontSize: 14, color: '#555', display: 'inline-flex', gap: 6, alignItems: 'center', marginBottom: 12 }}>
            <input type="checkbox" checked={onlyDue} onChange={(e) => setOnlyDue(e.target.checked)} /> Só com saldo a pagar
          </label>

          <div style={{ ...box, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Quem recebe</th><th style={th}>A pagar</th><th style={th}>Vendas de álbuns</th><th style={th}>Royalties de streaming</th><th style={th}>Já pago</th><th style={th}>Incentivos pagos</th><th style={th}>Dados bancários</th><th style={th}></th></tr></thead>
              <tbody>
                {loading && <tr><td style={td} colSpan={8}>A carregar…</td></tr>}
                {!loading && shown.length === 0 && <tr><td style={{ ...td, color: '#999' }} colSpan={8}>Ainda não há ganhos nem pagamentos registados.</td></tr>}
                {shown.map((r) => (
                  <tr key={payeeKey(r.payee)}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{payeeLabel(r.payee)}</div>
                      <div style={{ color: '#999', fontSize: 12 }}>#{r.payee.id} · {r.payee.email}</div>
                      {r.pending_requests > 0 && <div style={{ color: '#B26A00', fontSize: 12, fontWeight: 600 }}>Pedido de levantamento pendente</div>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{(r.balances || []).map((b) => <div key={b.currency} style={{ fontWeight: 700, color: b.balance > 0 ? '#2E7D32' : '#999' }}>{money(b.balance, b.currency)}</div>)}</td>
                    <td style={{ ...td, fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>{(r.balances || []).filter((b) => b.sales_earned > 0).map((b) => <div key={b.currency}>{money(b.sales_earned, b.currency)}</div>)}{!(r.balances || []).some((b) => b.sales_earned > 0) && '—'}</td>
                    <td style={{ ...td, fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>{(r.balances || []).filter((b) => b.royalties_earned > 0).map((b) => <div key={b.currency}>{money(b.royalties_earned, b.currency)}</div>)}{!(r.balances || []).some((b) => b.royalties_earned > 0) && '—'}</td>
                    <td style={{ ...td, fontSize: 12, color: '#777', whiteSpace: 'nowrap' }}>{(r.balances || []).filter((b) => b.paid > 0).map((b) => <div key={b.currency}>{money(b.paid, b.currency)}</div>)}{!(r.balances || []).some((b) => b.paid > 0) && '—'}</td>
                    <td style={td}>{r.incentives_paid > 0 ? money(r.incentives_paid, 'AOA') : '—'}</td>
                    <td style={{ ...td, fontSize: 12 }}>{r.account ? (r.account.complete ? <span style={{ color: '#2E7D32' }}>✓ {r.account.method === 'multicaixa_express' ? 'MCX' : 'IBAN'}</span> : <span style={{ color: '#B26A00' }}>Incompletos</span>) : <span style={{ color: '#C62828' }}>Em falta</span>}</td>
                    <td style={td}><button onClick={() => setOpen(r.payee)} style={{ padding: '5px 12px', borderRadius: 4, border: `1px solid ${PURPLE}`, background: 'transparent', color: PURPLE, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Abrir</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16, fontSize: 13, color: '#777' }}>
            Para pagar um incentivo a um artista que ainda não aparece aqui: abre-o pelo ID.{' '}
            <IdOpener onOpen={(id) => setOpen({ type: 'artist', id })} />
          </div>
        </>
      )}

      {open && <ArtistDetail key={payeeKey(open)} payee={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

// Withdrawal requests. "Marcar como pago" is for after the transfer: it records
// the payment and emails the payee. "Rejeitar" asks for a reason, which goes in the email.
function Requests({ onChanged, onOpen }) {
  const [status, setStatus] = useState('pending');
  const [list, setList] = useState(null);
  const [paying, setPaying] = useState(null);
  const [form, setForm] = useState({ method: 'bank_transfer', paid_at: today(), reference: '', notes: '' });
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const r = await izenuAPI.get('/admin/artist_payouts/requests.php', { params: { status } });
    setList(r.data.data || []);
  }, [status]);
  useEffect(() => { setList(null); load(); }, [load]);

  const startPay = (r) => {
    setMsg('');
    setPaying(r.id);
    setForm({ method: r.account?.method === 'multicaixa_express' ? 'multicaixa_express' : 'bank_transfer', paid_at: today(), reference: '', notes: '' });
  };

  const pay = async (r) => {
    setMsg('');
    try {
      await izenuAPI.post('/admin/artist_payouts/requests.php', { pay: r.id, ...form });
      setPaying(null);
      await load();
      onChanged();
    } catch (e) {
      setMsg(e.response?.data?.message || 'Não foi possível registar.');
    }
  };

  const reject = async (r) => {
    const reason = window.prompt(`Rejeitar o pedido de ${money(r.amount, r.currency)} de ${payeeLabel(r.payee)}?\n\nMotivo (vai no email):`);
    if (reason === null) return;
    try {
      await izenuAPI.post('/admin/artist_payouts/requests.php', { reject: r.id, reason });
      await load();
      onChanged();
    } catch (e) {
      window.alert(e.response?.data?.message || 'Não foi possível rejeitar.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: '#666' }}>Estado:</span>
        <select style={{ ...input, width: 180 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="pending">Pendentes</option>
          <option value="paid">Pagos</option>
          <option value="rejected">Rejeitados</option>
          <option value="cancelled">Cancelados</option>
          <option value="">Todos</option>
        </select>
      </div>
      <div style={{ ...box, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Pedido</th><th style={th}>Quem</th><th style={th}>Valor</th><th style={th}>Para onde</th><th style={th}>Estado</th><th style={th}></th></tr></thead>
          <tbody>
            {list === null && <tr><td style={td} colSpan={8}>A carregar…</td></tr>}
            {list && list.length === 0 && <tr><td style={{ ...td, color: '#999' }} colSpan={6}>{status === 'pending' ? 'Nenhum pedido por tratar.' : 'Nenhum pedido.'}</td></tr>}
            {(list || []).map((r) => (
              <tr key={r.id}>
                <td style={td}>#{r.id}<div style={{ color: '#999', fontSize: 12 }}>{fmtDate(r.requested_at)}</div></td>
                <td style={td}>
                  <button onClick={() => onOpen({ type: r.payee.type, id: r.payee.id })} style={{ border: 'none', background: 'none', padding: 0, color: PURPLE, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>{payeeLabel(r.payee)}</button>
                  <div style={{ color: '#999', fontSize: 12 }}>{r.payee.email}</div>
                </td>
                <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{money(r.amount, r.currency)}</td>
                <td style={{ ...td, minWidth: 240 }}>
                  <div style={{ fontSize: 12 }}>{r.account_snapshot || '—'}</div>
                  {r.account && ArtistAccountChanged(r) && <div style={{ fontSize: 11, color: '#B26A00' }}>Os dados bancários mudaram depois do pedido — confirma com a pessoa.</div>}
                </td>
                <td style={{ ...td, color: REQUEST_STATUS[r.status][1], fontWeight: 600 }}>{REQUEST_STATUS[r.status][0]}{r.note && <div style={{ color: '#777', fontSize: 12, fontWeight: 400 }}>{r.note}</div>}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {r.status === 'pending' && paying !== r.id && (
                    <>
                      <button onClick={() => startPay(r)} style={{ padding: '5px 10px', borderRadius: 4, border: 'none', background: PURPLE, color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Marcar como pago</button>
                      <button onClick={() => reject(r)} style={{ padding: '5px 10px', borderRadius: 4, border: '1px solid #ddd', background: '#fff', color: '#C62828', fontSize: 12, cursor: 'pointer' }}>Rejeitar</button>
                    </>
                  )}
                  {paying === r.id && (
                    <div style={{ display: 'grid', gap: 6, minWidth: 220 }}>
                      <select style={input} value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                        {Object.entries(METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      <input style={input} type="date" max={today()} value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} />
                      <input style={input} placeholder="Referência / nº da transferência" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                      <input style={input} placeholder="Notas internas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => pay(r)} style={{ padding: '6px 10px', borderRadius: 4, border: 'none', background: PURPLE, color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Confirmar pagamento</button>
                        <button onClick={() => setPaying(null)} style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
                      </div>
                      {msg && <div style={{ fontSize: 12, color: '#C62828' }}>{msg}</div>}
                      <div style={{ fontSize: 11, color: '#999' }}>Faz primeiro a transferência; isto regista-a e envia o email.</div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The account line saved with the request vs the current one. */
function ArtistAccountChanged(r) {
  const a = r.account;
  const now = a.method === 'multicaixa_express' ? `${a.holder_name || ''} · MCX ${a.mcx_phone || ''}` : [a.holder_name, a.bank_name, a.iban].filter(Boolean).join(' · ');
  return r.status === 'pending' && r.account_snapshot && now.trim() !== r.account_snapshot.trim();
}

function IdOpener({ onOpen }) {
  const [id, setId] = useState('');
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <input value={id} onChange={(e) => setId(e.target.value.replace(/\D/g, ''))} placeholder="ID do artista" style={{ ...input, width: 120, padding: '4px 8px' }} />
      <button disabled={!id} onClick={() => onOpen(Number(id))} style={{ padding: '4px 10px', borderRadius: 4, border: `1px solid ${PURPLE}`, background: 'transparent', color: PURPLE, cursor: 'pointer' }}>Abrir</button>
    </span>
  );
}
